// src/crm/CreatorFormPage.tsx
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { CollaborationType, ContactType } from '../lib/types'
import { useCreator, useCreateCreator, useUpdateCreator } from './api'
import { CONTACT_TYPE_LABELS } from './components/ContactTypeBadge'
import { SocialEditor, type SocialDraft } from './components/SocialEditor'

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--muted)',
  marginBottom: '0.375rem',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--elevated)',
  border: '1px solid var(--line-strong)',
  borderRadius: '7px',
  padding: '0.5rem 0.75rem',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  outline: 'none',
  transition: 'border-color 0.12s',
}

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
}

const sectionStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: '10px',
  padding: '1.25rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
}

const sectionTitle: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--dim)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  margin: '0 0 0.25rem',
}

const row2: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
}

interface FormState {
  displayName: string
  collaborationType: CollaborationType
  contactType: ContactType
  organization: string
  contactRole: string
  niche: string
  audienceCountry: string
  audienceLanguage: string
  defaultRate: string
  currency: string
  email: string
  notes: string
  source: 'manual' | 'application' | 'url'
  socials: SocialDraft[]
}

const DEFAULT_FORM: FormState = {
  displayName: '',
  collaborationType: 'audience',
  contactType: 'ugc_creator',
  organization: '',
  contactRole: '',
  niche: '',
  audienceCountry: '',
  audienceLanguage: '',
  defaultRate: '',
  currency: 'EUR',
  email: '',
  notes: '',
  source: 'manual',
  socials: [],
}

let socialKeyCounter = 100
function makeKey() { return `s-${++socialKeyCounter}` }

export function CreatorFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)

  const { data: existing, isLoading } = useCreator(id ?? '')
  const createCreator = useCreateCreator()
  const updateCreator = useUpdateCreator(id ?? '')

  const [form, setForm] = useState<FormState>(DEFAULT_FORM)
  const [error, setError] = useState<string | null>(null)

  // Pré-remplir le formulaire en mode édition
  useEffect(() => {
    if (existing) {
      setForm({
        displayName: existing.displayName,
        collaborationType: existing.collaborationType,
        contactType: existing.contactType,
        organization: existing.organization ?? '',
        contactRole: existing.contactRole ?? '',
        niche: existing.niche ?? '',
        audienceCountry: existing.audienceCountry ?? '',
        audienceLanguage: existing.audienceLanguage ?? '',
        defaultRate: existing.defaultRate != null ? String(existing.defaultRate) : '',
        currency: existing.currency,
        email: existing.email ?? '',
        notes: existing.notes ?? '',
        source: existing.source,
        socials: existing.socials.map((s) => ({
          _key: makeKey(),
          platform: s.platform,
          handle: s.handle,
          profileUrl: s.profileUrl,
          followers: s.followers,
          engagementRate: s.engagementRate,
        })),
      })
    }
  }, [existing])

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (!form.displayName.trim()) {
      setError('Le nom est requis.')
      return
    }

    const body = {
      displayName: form.displayName.trim(),
      // Champ masqué pour les contacts non-UGC : on force la valeur compatible
      // avec le CHECK existant de collaboration_type (migrations/0002).
      collaborationType: form.contactType === 'ugc_creator' ? form.collaborationType : 'audience',
      contactType: form.contactType,
      organization: form.organization.trim() || null,
      contactRole: form.contactRole.trim() || null,
      niche: form.niche.trim() || null,
      audienceCountry: form.audienceCountry.trim() || null,
      audienceLanguage: form.audienceLanguage.trim() || null,
      defaultRate: form.defaultRate ? Number(form.defaultRate) : null,
      currency: form.currency || 'EUR',
      email: form.email.trim() || null,
      notes: form.notes.trim() || null,
      source: form.source,
      socials: form.socials.map((s) => ({
        platform: s.platform,
        handle: s.handle,
        profileUrl: s.profileUrl,
        followers: s.followers,
        engagementRate: s.engagementRate,
      })),
    }

    try {
      if (isEdit) {
        await updateCreator.mutateAsync(body)
        navigate('/crm/contacts')
      } else {
        await createCreator.mutateAsync(body)
        navigate('/crm/contacts')
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Une erreur est survenue.')
      }
    }
  }

  const isPending = createCreator.isPending || updateCreator.isPending

  if (isEdit && isLoading) {
    return (
      <div style={{ padding: '2rem 2.5rem' }}>
        <p style={{ color: 'var(--muted)' }}>Chargement…</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem 2.5rem', maxWidth: '680px' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.75rem' }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '7px',
            padding: '0.375rem 0.625rem',
            color: 'var(--muted)',
            fontSize: '0.8125rem',
            cursor: 'pointer',
          }}
        >
          ← Retour
        </button>
        <h1 style={{
          fontSize: '1.375rem',
          fontWeight: 600,
          color: 'var(--fg)',
          margin: 0,
          letterSpacing: '-0.025em',
        }}>
          {isEdit ? 'Modifier le contact' : 'Nouveau contact'}
        </h1>
      </div>

      <form onSubmit={(e) => { void handleSubmit(e) }} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Section identité */}
        <section style={sectionStyle}>
          <p style={sectionTitle}>Identité</p>

          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="displayName">Nom d'affichage *</label>
            <input
              id="displayName"
              type="text"
              value={form.displayName}
              onChange={(e) => set('displayName', e.target.value)}
              placeholder="Marie Dupont"
              required
              style={inputStyle}
            />
          </div>

          <div style={row2}>
            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="contactType">Type de contact</label>
              <select
                id="contactType"
                value={form.contactType}
                onChange={(e) => set('contactType', e.target.value as ContactType)}
                style={inputStyle}
              >
                {Object.entries(CONTACT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Source</label>
              <select
                value={form.source}
                onChange={(e) => set('source', e.target.value as FormState['source'])}
                style={inputStyle}
              >
                <option value="manual">Saisie manuelle</option>
                <option value="application">Candidature</option>
                <option value="url">URL profil</option>
              </select>
            </div>
          </div>

          {form.contactType === 'ugc_creator' ? (
            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="collaborationType">Type de collaboration</label>
              <select
                id="collaborationType"
                value={form.collaborationType}
                onChange={(e) => set('collaborationType', e.target.value as CollaborationType)}
                style={inputStyle}
              >
                <option value="audience">Audience</option>
                <option value="production">Production</option>
                <option value="both">Les deux</option>
              </select>
            </div>
          ) : (
            <div style={row2}>
              <div style={fieldStyle}>
                <label style={labelStyle} htmlFor="organization">Organisation</label>
                <input
                  id="organization"
                  type="text"
                  value={form.organization}
                  onChange={(e) => set('organization', e.target.value)}
                  placeholder="MacGeneration, 9to5Mac…"
                  style={inputStyle}
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle} htmlFor="contactRole">Rôle</label>
                <input
                  id="contactRole"
                  type="text"
                  value={form.contactRole}
                  onChange={(e) => set('contactRole', e.target.value)}
                  placeholder="Rédacteur en chef, journaliste…"
                  style={inputStyle}
                />
              </div>
            </div>
          )}

          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="niche">Niche / Domaine</label>
            <input
              id="niche"
              type="text"
              value={form.niche}
              onChange={(e) => set('niche', e.target.value)}
              placeholder="Fitness, Tech, Lifestyle…"
              style={inputStyle}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="email">E-mail</label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="creator@example.com"
              style={inputStyle}
            />
          </div>
        </section>

        {/* Section audience */}
        <section style={sectionStyle}>
          <p style={sectionTitle}>Audience</p>
          <div style={row2}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Pays de l'audience</label>
              <input
                type="text"
                value={form.audienceCountry}
                onChange={(e) => set('audienceCountry', e.target.value)}
                placeholder="FR"
                maxLength={2}
                style={inputStyle}
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Langue de l'audience</label>
              <input
                type="text"
                value={form.audienceLanguage}
                onChange={(e) => set('audienceLanguage', e.target.value)}
                placeholder="fr"
                maxLength={5}
                style={inputStyle}
              />
            </div>
          </div>
        </section>

        {/* Section tarif */}
        <section style={sectionStyle}>
          <p style={sectionTitle}>Tarif par défaut</p>
          <div style={row2}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Montant</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={form.defaultRate}
                onChange={(e) => set('defaultRate', e.target.value)}
                placeholder="500"
                style={inputStyle}
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Devise</label>
              <input
                type="text"
                value={form.currency}
                onChange={(e) => set('currency', e.target.value.toUpperCase())}
                placeholder="EUR"
                maxLength={3}
                style={inputStyle}
              />
            </div>
          </div>
        </section>

        {/* Section socials */}
        <section style={sectionStyle}>
          <SocialEditor
            value={form.socials}
            onChange={(socials) => set('socials', socials)}
          />
        </section>

        {/* Notes */}
        <section style={sectionStyle}>
          <p style={sectionTitle}>Notes internes</p>
          <div style={fieldStyle}>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Observations, contexte…"
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
            />
          </div>
        </section>

        {/* Erreur */}
        {error && (
          <p style={{
            background: 'rgba(217,83,30,0.1)',
            border: '1px solid rgba(217,83,30,0.25)',
            borderRadius: '7px',
            padding: '0.625rem 0.875rem',
            color: 'var(--arancio)',
            fontSize: '0.875rem',
            margin: 0,
          }}>
            {error}
          </p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              background: 'var(--elevated)',
              border: '1px solid var(--line)',
              borderRadius: '7px',
              padding: '0.5rem 1rem',
              color: 'var(--muted)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isPending}
            style={{
              background: isPending ? 'var(--dim)' : 'var(--arancio)',
              border: 'none',
              borderRadius: '7px',
              padding: '0.5rem 1.25rem',
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              transition: 'background 0.12s',
            }}
          >
            {isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  )
}
